import React from "react";
import { FileText, Leaf } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
    <div className="mt-3 text-foreground/80 leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsPage = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="flex items-center gap-3">
      <span className="grid place-items-center h-11 w-11 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <FileText className="h-5 w-5" />
      </span>
      <span className="text-xs uppercase tracking-widest text-muted-foreground">Legal</span>
    </div>
    <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight">Terms of Service</h1>
    <p className="mt-3 text-muted-foreground">Last updated: February 23, 2026</p>

    <p className="mt-8 leading-relaxed">
      Welcome to Ecowallet. By creating an account or using our platform, you agree to be bound by these Terms of
      Service. Please read them carefully.
    </p>

    <Section title="1. Who We Are">
      <p>
        Ecowallet ("we", "us", "our") is a sustainable shopping companion that helps users discover eco-friendly
        products, earn cashback via affiliate partnerships, and track their environmental impact through Green Points.
      </p>
    </Section>

    <Section title="2. Eligibility">
      <p>
        You must be at least 18 years old (or the age of majority in your jurisdiction) to create an Ecowallet
        account. By registering, you confirm that the information you provide is accurate and complete.
      </p>
    </Section>

    <Section title="3. Your Account">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>You are responsible for keeping your password secure.</li>
        <li>You are responsible for all activity that occurs under your account.</li>
        <li>One person = one account. Multiple accounts to game rewards is prohibited.</li>
        <li>We may suspend or terminate accounts that violate these terms.</li>
      </ul>
    </Section>

    <Section title="4. Cashback & Green Points">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Cashback and Green Points are credited only when a purchase is validated by the affiliate merchant.</li>
        <li>Returned, cancelled, or fraudulent orders will result in reversal of cashback and Green Points.</li>
        <li>Green Points have no cash value outside the Ecowallet rewards catalog.</li>
        <li>Points may expire 24 months after they are earned if the account is inactive.</li>
      </ul>
    </Section>

    <Section title="5. Sustainability Scores">
      <p>
        Sustainability scores are calculated using publicly available product information and our transparent
        methodology. Scores are informational and should not be interpreted as certifications or endorsements.
      </p>
    </Section>

    <Section title="6. Rewards & Redemptions">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Gift cards and coupons are subject to the terms of the issuing partner.</li>
        <li>Impact rewards (tree plantation, NGO donations) are executed via our verified partners.</li>
        <li>Redemptions are final. Points cannot be reinstated once redeemed.</li>
      </ul>
    </Section>

    <Section title="7. Prohibited Conduct">
      <p>You agree not to:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Use bots, scrapers, or automated tools to interact with the platform.</li>
        <li>Attempt to manipulate leaderboards, badges, or reward calculations.</li>
        <li>Post abusive, unlawful, or misleading content.</li>
        <li>Reverse-engineer or resell any part of the service.</li>
      </ul>
    </Section>

    <Section title="8. Intellectual Property">
      <p>
        All content on Ecowallet — including the Ecowallet name, logo, sustainability methodology, and platform
        design — is our property or licensed to us. You may not copy or reuse it without written permission.
      </p>
    </Section>

    <Section title="9. Disclaimers">
      <p>
        Ecowallet acts as a discovery and rewards layer. We do not manufacture, sell, or ship products directly. All
        purchases are between you and the merchant. We make no warranty regarding product quality, delivery, or
        availability.
      </p>
    </Section>

    <Section title="10. Limitation of Liability">
      <p>
        To the fullest extent permitted by law, Ecowallet is not liable for any indirect, incidental, or
        consequential damages arising from your use of the platform.
      </p>
    </Section>

    <Section title="11. Termination">
      <p>
        You may close your account at any time. We may suspend or terminate your access if you breach these terms or
        engage in fraudulent activity.
      </p>
    </Section>

    <Section title="12. Governing Law">
      <p>
        These terms are governed by the laws of India. Any dispute shall be resolved in the courts of Bengaluru,
        Karnataka.
      </p>
    </Section>

    <Section title="13. Contact">
      <p>
        Questions? Email <a href="mailto:legal@ecowallet.in" className="text-primary hover:underline">legal@ecowallet.in</a>.
      </p>
    </Section>

    <div className="mt-14 card-premium p-6 flex items-center gap-4">
      <Leaf className="h-6 w-6 text-emerald-600" />
      <p className="text-sm text-muted-foreground">
        Thank you for shopping with purpose. Every choice matters.
      </p>
    </div>
  </div>
);

export default TermsPage;
